import { useCallback, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ClipboardList, Pencil, Trash2, FileDown, ArrowRight, Loader2, MessageCircle, CalendarPlus } from "lucide-react";
import { DeslocamentoDialog } from "@/components/DeslocamentoDialog";
import { NaoConcluirButton } from "@/components/NaoConcluir";
import { BackLink, DetailHeader, Panel, InfoGrid, ActionButton, ConfirmDelete, inputCls } from "@/components/Bits";
import { OSPhotos } from "@/components/OSPhotos";
import { Signatures } from "@/components/Signatures";
import { WhatsAppShare } from "@/components/WhatsAppShare";
import { OS_STATUS, StatusBadge, nextStatus, statusInfo } from "@/config/os";
import { tipoIcon } from "@/config/equipamentos";
import { useAuth } from "@/context/AuthContext";
import { useFetch, fmtDate, openPdf } from "@/lib/useFetch";
import { api, errMsg } from "@/lib/api";

const StatusBar = ({ os, onChange, isAdmin, onNeedSign }) => {
  let next = nextStatus(os.status);
  if (next?.value === "finalizada" && !isAdmin) next = null;
  const set = async (status) => {
    if (status === "finalizada" && !os.assinatura_cliente) { toast.error("Colete a assinatura do cliente antes de encerrar"); onNeedSign(); return; }
    try { const { data } = await api.patch(`/ordens/${os.os_id}/status`, { status }); onChange(data); toast.success(`Status: ${statusInfo(status).label}`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const opts = OS_STATUS.filter((s) => (isAdmin || s.value !== "finalizada" || os.status === "finalizada") && (s.value !== "nao_concluida" || os.status === "nao_concluida"));
  return (
    <Panel testId="os-status-bar">
      <div className="flex flex-col sm:flex-row gap-3">
        {next && <button onClick={() => set(next.value)} data-testid="os-next-status"
          className="h-14 w-full sm:flex-1 rounded-xl bg-[#1E4D2B] text-white font-semibold flex items-center justify-center gap-2 hover:bg-[#163A20] active:scale-[0.98]">
          {next.value === "finalizada" ? "Encerrar OS" : `Avançar para: ${next.label}`} <ArrowRight className="h-5 w-5" /></button>}
        <select value={os.status} onChange={(e) => set(e.target.value)} className={`${inputCls} sm:w-64`} data-testid="os-status-select">
          {opts.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>
      {!isAdmin && <p className="mt-3 text-xs text-[#5C6B66]">Somente o administrador encerra a OS, após a assinatura do cliente.</p>}
    </Panel>
  );
};

const BRL = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const ValoresPanel = ({ os }) => {
  const pecas = os.pecas.filter((p) => p.valor_unitario);
  const total = (os.valor_servico || 0) + pecas.reduce((a, p) => a + p.valor_unitario * p.quantidade, 0);
  return (
    <Panel title="Valores" testId="os-valores">
      <ul className="divide-y divide-[#E2DDD3]">
        <li className="py-3 flex justify-between"><span>Serviço executado</span><b data-testid="os-valor-servico">{BRL(os.valor_servico)}</b></li>
        {pecas.map((p, i) => <li key={i} className="py-3 flex justify-between gap-3"><span className="truncate">{p.quantidade}x {p.descricao}</span><b>{BRL(p.valor_unitario * p.quantidade)}</b></li>)}
        <li className="py-3 flex justify-between text-lg"><span className="font-bold">Total</span><b className="text-[#1E4D2B]" data-testid="os-valor-total">{BRL(total)}</b></li>
      </ul>
    </Panel>
  );
};

const TextBlock = ({ label, value }) => (
  <div><p className="text-[11px] font-bold uppercase tracking-wider text-[#5C6B66]">{label}</p><p className="mt-1 whitespace-pre-line">{value || "—"}</p></div>
);

export default function OSDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { user, canAccess } = useAuth();
  const { data: os, reload, setData } = useFetch(`/ordens/${id}`);
  const [del, setDel] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [askSign, setAskSign] = useState(params.get("assinar") === "1");
  const [aviso, setAviso] = useState(false);
  const [autoSend, setAutoSend] = useState(false);
  const askDone = useCallback(() => { setAskSign(false); if (params.get("assinar")) setParams({}, { replace: true }); }, [params, setParams]);
  if (!os || os.os_id !== id) return null;

  const onStatus = (data) => {
    setData(data);
    if (data.status === "aguardando_aprovacao" && !data.assinatura_cliente) setAskSign(true);
    if (data.status === "finalizada") setAutoSend(true);
    if (data.status === "em_deslocamento") setAviso(true);
  };
  const full = user.role !== "cliente";
  const isAdmin = user.role === "admin";
  const LinkIf = ({ ok, to, children }) => (ok ? <Link className="text-[#1E4D2B] font-semibold hover:underline" to={to}>{children}</Link> : <span>{children}</span>);

  const pdf = async () => {
    setPdfBusy(true);
    try { await openPdf(`/ordens/${id}/pdf`, `${os.numero}.pdf`); } catch (e) { toast.error("Falha ao gerar PDF"); }
    setPdfBusy(false);
  };
  const reagendar = async () => {
    try { const { data } = await api.post(`/ordens/${id}/reagendar`); toast.success(`Nova OS ${data.numero} criada`); navigate(`/ordens-servico/${data.os_id}`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    try { await api.delete(`/ordens/${id}`); toast.success("OS excluída"); navigate("/ordens-servico"); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="os-detail-page">
      <BackLink to="/ordens-servico" label="Ordens de Serviço" />
      <DetailHeader eyebrow={os.categoria_nome || "Ordem de serviço"} title={os.numero} icon={ClipboardList}
        subtitle={<span className="inline-flex items-center gap-2 mt-1"><StatusBadge status={os.status} testId="os-status-badge" /> {fmtDate(os.data)} {os.horario}</span>}
        actions={<>
          <ActionButton icon={pdfBusy ? Loader2 : FileDown} variant="earth" onClick={pdf} disabled={pdfBusy} data-testid="os-pdf-button">Gerar PDF</ActionButton>
          {os.pode_editar && <ActionButton icon={Pencil} onClick={() => navigate(`/ordens-servico/${id}/editar`)} data-testid="os-edit-button">Editar</ActionButton>}
          {user.role === "admin" && <ActionButton icon={Trash2} variant="danger" onClick={() => setDel(true)} data-testid="os-delete-button">Excluir</ActionButton>}
        </>} />
      <div className="space-y-5">
        {os.pode_editar && <StatusBar os={os} onChange={onStatus} isAdmin={isAdmin} onNeedSign={() => setAskSign(true)} />}
        {os.motivo_nao_conclusao && (
          <div className="rounded-2xl bg-[#F5E6E0] border border-[#B05A40]/40 p-4" data-testid="os-nao-concluida-banner">
            <p className="font-bold text-[#8C3F28]">OS não concluída — sem custo para o cliente</p>
            <p className="text-sm mt-1">{os.motivo_nao_conclusao.texto} · {os.motivo_nao_conclusao.user_name}</p>
            {os.reagendada_os_id && <Link to={`/ordens-servico/${os.reagendada_os_id}`} className="inline-block mt-2 text-sm font-semibold text-[#1E4D2B] hover:underline" data-testid="os-reagendada-link">Reagendada como {os.reagendada_numero} →</Link>}
            {isAdmin && os.status === "nao_concluida" && !os.reagendada_os_id && (
              <button onClick={reagendar} data-testid="os-reagendar-button" className="mt-3 h-14 w-full rounded-xl bg-[#1E4D2B] text-white font-semibold flex items-center justify-center gap-2 hover:bg-[#163A20] active:scale-[0.98]">
                <CalendarPlus className="h-5 w-5" /> Reagendar (nova OS com os mesmos dados)</button>
            )}
          </div>
        )}
        {os.origem_os_id && full && <Link to={`/ordens-servico/${os.origem_os_id}`} className="block rounded-2xl bg-[#E1F0FA] p-4 text-sm font-semibold text-[#015F8C] hover:underline" data-testid="os-origem-link">Reagendada a partir da {os.origem_numero} (não concluída) →</Link>}
        {user.role !== "cliente" && <NaoConcluirButton os={os} onDone={setData} showButton={os.pode_editar && !["finalizada", "nao_concluida"].includes(os.status)} />}
        {os.status === "em_deslocamento" && user.role !== "cliente" && (
          <button onClick={() => setAviso(true)} data-testid="os-aviso-button"
            className="h-14 w-full rounded-xl bg-[#25D366] text-white font-semibold flex items-center justify-center gap-2 hover:opacity-90">
            <MessageCircle className="h-5 w-5" /> Avisar cliente que estou a caminho
          </button>
        )}
        <DeslocamentoDialog open={aviso} onOpenChange={setAviso} os={os} />
        {os.status === "finalizada" && user.role === "admin" && <WhatsAppShare os={os} auto={autoSend} onAutoDone={() => setAutoSend(false)} />}
        <Panel title="Atendimento" testId="os-info">
          <InfoGrid items={[
            ["Cliente", <LinkIf ok={canAccess("clientes")} to={`/clientes/${os.cliente_id}`}>{os.cliente?.nome}</LinkIf>],
            ["Fazenda", <LinkIf ok={canAccess("fazendas")} to={`/fazendas/${os.fazenda_id}`}>{os.fazenda?.nome}</LinkIf>],
            ["Município / UF", [os.fazenda?.municipio, os.fazenda?.estado].filter(Boolean).join(" / ")],
            ["Técnico responsável", os.tecnico_nome], ["Colaboradores", os.colaboradores.map((c) => c.name).join(", ")],
            ["Data / horário", `${fmtDate(os.data)} ${os.horario || ""}`], ["Quilometragem", os.km != null ? `${os.km} km` : null],
            ["Horas trabalhadas", os.horas_trabalhadas != null ? `${os.horas_trabalhadas} h` : null], ["Categoria", os.categoria_nome]]} />
        </Panel>
        <Panel title={`Equipamentos (${os.equipamentos.length})`} testId="os-equipamentos">
          <div className="grid sm:grid-cols-2 gap-2">
            {os.equipamentos.map((e) => { const I = tipoIcon(e.tipo); const inner = (<>
                <I className="h-5 w-5 text-[#1E4D2B]" /><span className="flex-1 font-semibold truncate">{e.titulo}</span><span className="text-xs text-[#5C6B66]">{e.codigo}</span></>);
              const cls = "min-w-0 flex items-center gap-3 min-h-[56px] px-3 rounded-xl border border-[#E2DDD3] hover:border-[#1E4D2B]";
              return canAccess("equipamentos") ? <Link key={e.equipamento_id} to={`/equipamentos/${e.equipamento_id}`} className={cls}>{inner}</Link>
                : <div key={e.equipamento_id} className={cls}>{inner}</div>; })}
            {os.equipamentos.length === 0 && <p className="text-[#5C6B66]">Nenhum equipamento selecionado.</p>}
          </div>
        </Panel>
        {full && <Panel title="Descrição do serviço" testId="os-descricao">
          <div className="grid md:grid-cols-2 gap-5">
            <TextBlock label="Serviço solicitado" value={os.servico_solicitado} /><TextBlock label="Problema relatado" value={os.problema_relatado} />
            <TextBlock label="Diagnóstico" value={os.diagnostico} /><TextBlock label="Serviço realizado" value={os.servico_realizado} />
            <TextBlock label="Observações" value={os.observacoes} />
          </div>
        </Panel>}
        {!full && <Panel title="Seu pedido" testId="os-pedido">
          <div className="grid md:grid-cols-2 gap-5">
            <TextBlock label="Serviço solicitado" value={os.servico_solicitado} /><TextBlock label="Problema relatado" value={os.problema_relatado} />
          </div>
        </Panel>}
        {full && <Panel title={`Peças utilizadas (${os.pecas.length})`} testId="os-pecas">
          {os.pecas.length === 0 ? <p className="text-[#5C6B66]">Nenhuma peça registrada.</p> : (
            <ul className="divide-y divide-[#E2DDD3]">{os.pecas.map((p, i) => (
              <li key={i} className="py-3 flex justify-between gap-3"><span><b>{p.descricao}</b>{p.codigo && <span className="text-[#5C6B66]"> · {p.codigo}</span>}</span><span className="font-semibold">{p.quantidade}x</span></li>))}</ul>
          )}
        </Panel>}
        {user.role !== "tecnico" && "valor_servico" in os && <ValoresPanel os={os} />}
        {full && <OSPhotos os={os} onChange={reload} />}
        {full && <Signatures os={os} role={user.role} onChange={reload} ask={askSign} onAskDone={askDone} />}
        <Panel title="Linha do tempo" testId="os-timeline">
          <ol className="border-l-2 border-[#E2DDD3] ml-2 space-y-4">
            {[...os.status_log].reverse().map((s, i) => (
              <li key={i} className="pl-5 relative"><span className="absolute -left-[9px] top-1 h-4 w-4 rounded-full bg-[#1E4D2B] border-2 border-white" />
                <StatusBadge status={s.status} /><p className="text-xs text-[#5C6B66] mt-1">{new Date(s.at).toLocaleString("pt-BR")} · {s.user_name}{s.nota ? ` · ${s.nota}` : ""}</p></li>
            ))}
          </ol>
        </Panel>
      </div>
      <ConfirmDelete open={del} onOpenChange={setDel} title="Excluir OS?" description="A OS será removida do histórico. Esta ação não pode ser desfeita." onConfirm={remove} />
    </div>
  );
}
